/**
 * Login — Entra (MSAL) sign-in, plus an email one-time-code path for users on
 * personal/unmanaged devices the IdP blocks. In mock mode, redirects to /.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { consumeOtpExpiredNotice, useAuth } from '@/auth/AuthProvider';
import { requestOtp, verifyOtp } from '@/api/auth';
import { ApiError } from '@/lib/api';

export function Login() {
  const { signIn, isAuthenticated, mode } = useAuth();
  const nav = useNavigate();
  // Sent back here because a one-time-code session expired? Say so, and open
  // straight onto the code flow — that's the path this user was on.
  const [sessionExpired] = useState(() => consumeOtpExpiredNotice());
  const [showOtp, setShowOtp] = useState(sessionExpired);

  useEffect(() => {
    if (isAuthenticated) nav('/', { replace: true });
  }, [isAuthenticated, nav]);

  if (mode === 'mock') {
    return (
      <div className="h-full grid place-items-center p-6 bg-fill-soft">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold">Vari Field Management System</h1>
          <p className="text-ink-dim text-sm">
            Local dev with mock auth. You're already signed in — pick a role in the sidebar
            switcher to try different views.
          </p>
          <Button onClick={() => nav('/')}>Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full grid place-items-center p-6 bg-fill-soft">
      <div className="max-w-md w-full bg-bg border border-line rounded-lg p-8 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Vari Field Management System</h1>
          <p className="text-ink-dim text-sm">Sign in with your Vari account.</p>
        </div>
        {sessionExpired && (
          <p className="text-sm text-ink-dim bg-fill-soft border border-line rounded-md px-3 py-2">
            Your sign-in session expired — please sign in again.
          </p>
        )}
        {showOtp ? (
          <OtpFlow onBack={() => setShowOtp(false)} />
        ) : (
          <div className="space-y-3">
            <Button className="w-full" onClick={() => void signIn()}>
              Sign in with Microsoft
            </Button>
            <button
              type="button"
              onClick={() => setShowOtp(true)}
              className="w-full text-sm text-ink-dim hover:text-ink underline"
            >
              Sign in with a one-time code
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Email → 6-digit code → OTP session. The notice is intentionally generic so it
 *  never reveals whether an account exists. */
function OtpFlow({ onBack }: { onBack: () => void }) {
  const { signInWithOtp } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setBusy(true);
    setError(null);
    try {
      await requestOtp(addr);
      setStep('code');
    } catch {
      setError('Something went wrong sending your code. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { token } = await verifyOtp(email.trim(), code.trim());
      signInWithOtp(token);
      nav('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code is invalid or has expired.');
    } finally {
      setBusy(false);
    }
  }

  if (step === 'email') {
    return (
      <form onSubmit={send} className="space-y-3">
        <div>
          <label htmlFor="otp-email" className="text-sm font-medium">
            Work email
          </label>
          <Input
            id="otp-email"
            type="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@vari.com"
            className="mt-1"
          />
          <p className="text-[11px] text-ink-faint mt-1">
            We'll email you a 6-digit code if you have an account.
          </p>
        </div>
        {error && <p className="text-xs text-status-blocked-fg">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy || !email.trim()}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send code
        </Button>
        <button
          type="button"
          onClick={onBack}
          className="w-full text-xs text-ink-faint hover:text-ink"
        >
          Back to sign-in options
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="otp-code" className="text-sm font-medium">
          Enter your code
        </label>
        <Input
          id="otp-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="------"
          className="mt-1 text-center text-lg tracking-[0.4em]"
        />
        <p className="text-[11px] text-ink-faint mt-1">
          If <span className="font-medium">{email.trim()}</span> has an account, a code is on its
          way. It expires in 10 minutes.
        </p>
      </div>
      {error && <p className="text-xs text-status-blocked-fg">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy || !code.trim()}>
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verify &amp; sign in
      </Button>
      <button
        type="button"
        onClick={() => {
          setStep('email');
          setCode('');
          setError(null);
        }}
        className="w-full text-xs text-ink-faint hover:text-ink"
      >
        Use a different email
      </button>
    </form>
  );
}
